import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Drawer, FormControl, Grid, IconButton, InputLabel, LinearProgress,
  ListItemText, MenuItem, Paper, Select, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import MemoryIcon from '@mui/icons-material/Memory';
import QueueIcon from '@mui/icons-material/Queue';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ListIcon from '@mui/icons-material/List';
import FilterListIcon from '@mui/icons-material/FilterList';
import BlenderIcon from '@mui/icons-material/Blender';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import InventoryIcon from '@mui/icons-material/Inventory';
import HistoryIcon from '@mui/icons-material/History';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import { machineService, productionOrderService, customerOrderService, productService,
  qualityCheckService, machineBreakService, staffService,
  maintenanceTicketService, checkMaintenanceAlerts, getDowntimeReport,
  workShiftService, dailyMachineOperatorService, operatorShiftReportService } from '../services/api';
import api from '../services/api';
import PlanOrderDialog from '../components/PlanOrderDialog';
import BuildIcon from '@mui/icons-material/Build';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PauseIcon from '@mui/icons-material/Pause';
import BarChartIcon from '@mui/icons-material/BarChart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip, Legend, ResponsiveContainer } from 'recharts';
import { TicketCard, STATUSES as MT_STATUSES, STATUS_LABEL as MT_STATUS_LABEL } from './MaintenanceKanbanPage';
import { orderRefFromRow } from '../utils/orderLabel';

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtDt = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const STATUS_CFG = {
  planned:     { label: 'Заплановано', bg: '#e3f2fd', color: '#0d47a1' },
  in_progress: { label: 'В роботі',    bg: '#e8f5e9', color: '#1b5e20' },
  paused:      { label: 'Пауза',       bg: '#fff3e0', color: '#e65100' },
  completed:   { label: 'Завершено',   bg: '#f3e5f5', color: '#4a148c' },
  cancelled:   { label: 'Скасовано',   bg: '#fce4ec', color: '#880e4f' },
};

function StatusChip({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: '#eee', color: '#555' };
  return (
    <Chip label={cfg.label} size="small"
      sx={{ fontWeight: 700, fontSize: 11, bgcolor: cfg.bg, color: cfg.color }} />
  );
}

function OnTimeBadge({ order }) {
  if (order.status !== 'in_progress') return null;
  const end = order.planned_end ? new Date(order.planned_end).getTime() : null;
  if (!end) return null;
  const late = Date.now() > end;
  return (
    <Chip size="small" icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important' }} />}
      label={late ? 'відстає' : 'вчасно'}
      sx={{ fontWeight: 700, fontSize: 10, bgcolor: late ? '#fce4ec' : '#e8f5e9', color: late ? '#c62828' : '#2e7d32' }} />
  );
}

function ProgressBar({ order }) {
  const total = order.quantity || 0;
  const done = order.produced_fact || 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" color="text.secondary">{done} / {total} шт</Typography>
        <Typography variant="caption" color="text.secondary">{pct}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: 2, height: 5 }} />
    </Box>
  );
}

function RealtimeCounter({ order }) {
  const [count, setCount] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (order.status !== 'in_progress' || !order.launched_at) {
      setCount(null);
      return;
    }
    const cycleSec = order.press_form_production_time_seconds;
    const cavities = order.product_number_of_cavities || order.press_form_number_of_cavities;
    if (!cycleSec || !cavities) { setCount(null); return; }

    const calc = () => {
      const elapsedSec = (Date.now() - new Date(order.launched_at).getTime()) / 1000;
      return Math.floor(elapsedSec / cycleSec) * cavities;
    };

    setCount(calc());
    timerRef.current = setInterval(() => setCount(calc()), 8000);
    return () => clearInterval(timerRef.current);
  }, [order.status, order.launched_at, order.press_form_production_time_seconds, order.product_number_of_cavities, order.press_form_number_of_cavities]);

  if (count === null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      <FiberManualRecordIcon sx={{ fontSize: 8, color: '#2e7d32', animation: 'pulse 1.5s infinite' }} />
      <Typography variant="caption" color="text.secondary">
        Реал-тайм: <strong style={{ color: '#1b5e20' }}>{count.toLocaleString('uk-UA')} шт</strong>
      </Typography>
    </Box>
  );
}

// ─── Kanban: Machine tile ─────────────────────────────────────────────────────

function MachineTile({ machine, orders, onClick }) {
  const active = orders.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status));
  const current = active.find((o) => o.status === 'in_progress') || active.find((o) => o.status === 'paused') || active[0];
  const queueCount = active.filter((o) => o.id !== current?.id).length;
  const borderColor = current?.status === 'in_progress' ? '#2e7d32'
    : current?.status === 'paused' ? '#e65100'
    : current ? '#1565c0' : '#e0e0e0';

  return (
    <Paper elevation={2} onClick={() => onClick(machine, active)} sx={{
      borderRadius: 3, p: 2, mb: 1.5, cursor: 'pointer',
      border: `2px solid ${borderColor}`, transition: 'all 0.15s',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: 4 },
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: '#1a237e', fontSize: 12, fontWeight: 700 }}>
            {(machine.identifier || machine.name || '?')[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
              {machine.identifier || machine.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">{machine.name}</Typography>
          </Box>
        </Stack>
        {queueCount > 0 && (
          <Chip label={`+${queueCount}`} size="small" sx={{ fontSize: 10, bgcolor: '#e8eaf6', color: '#283593' }} />
        )}
      </Stack>
      {current ? (
        <>
          {current.customer_order_number && (
            <Typography variant="caption" color="#5c6bc0" fontWeight={600}>№ {current.customer_order_number}</Typography>
          )}
          <Typography variant="body2" fontWeight={700} mb={0.5} noWrap>{current.product_name || '—'}</Typography>
          <Stack direction="row" spacing={0.5} mb={1} flexWrap="wrap">
            <StatusChip status={current.status} />
            <OnTimeBadge order={current} />
          </Stack>
          {['in_progress', 'paused'].includes(current.status) && <ProgressBar order={current} />}
          <RealtimeCounter order={current} />
          {current.status === 'planned' && (
            <Typography variant="caption" color="text.secondary">Старт: {fmtDt(current.planned_start)}</Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Вільний</Typography>
      )}
    </Paper>
  );
}

function QueueTile({ order, onClick }) {
  return (
    <Paper elevation={1} onClick={() => onClick(order)} sx={{
      borderRadius: 3, p: 1.5, mb: 1, cursor: 'pointer',
      border: '2px solid transparent', transition: 'all 0.15s',
      '&:hover': { borderColor: '#1a237e', boxShadow: 3 },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="#5c6bc0" fontWeight={600}>
            {order.customer_order_number ? `№ ${order.customer_order_number}` : '—'}
          </Typography>
          <Typography variant="body2" fontWeight={700} noWrap>{order.product_name}</Typography>
          {order.kit_label && (
            <Typography variant="caption" color="#6a1b9a" fontWeight={600} noWrap display="block" title={order.kit_label}>
              {order.kit_label}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {order.machine_identifier} · {order.press_form_identifier || '—'}
          </Typography>
        </Box>
        <StatusChip status={order.status} />
      </Stack>
      <Stack direction="row" justifyContent="space-between" mt={0.5}>
        <Typography variant="caption" color="text.secondary">{fmtDt(order.planned_start)}</Typography>
        <Typography variant="caption" color="text.secondary">{fmtDt(order.planned_end)}</Typography>
      </Stack>
    </Paper>
  );
}

function UnscheduledTile({ coLine, onClick }) {
  return (
    <Paper elevation={1} onClick={() => onClick(coLine)} sx={{
      borderRadius: 3, p: 1.5, mb: 1, cursor: 'pointer',
      border: '2px solid transparent', transition: 'all 0.15s',
      '&:hover': { borderColor: '#388e3c', boxShadow: 3 },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="#5c6bc0" fontWeight={600}>№ {coLine.orderNumber}</Typography>
          <Typography variant="body2" fontWeight={700} noWrap>{coLine.product_name}</Typography>
          <Typography variant="caption" color="text.secondary">{coLine.customer_name}</Typography>
        </Box>
        <Chip label="Не в плані" size="small" sx={{ fontSize: 10, bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 700 }} />
      </Stack>
      {coLine.deadline && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          до {fmtDate(coLine.deadline)}
        </Typography>
      )}
    </Paper>
  );
}

// ─── Detail components ────────────────────────────────────────────────────────

function OrderDetailCard({ order }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="#5c6bc0" fontWeight={600}>
          {order.customer_order_number ? `№ ${order.customer_order_number}` : '—'}
        </Typography>
        <StatusChip status={order.status} />
      </Stack>
      <Typography variant="body2" fontWeight={700} mb={0.3}>{order.product_name}</Typography>
      {order.kit_label && (
        <Typography variant="caption" color="#6a1b9a" fontWeight={600} display="block" mb={0.3}>
          {order.kit_label}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        {order.machine_identifier} · {order.press_form_identifier || '—'}
      </Typography>
      {['in_progress', 'paused'].includes(order.status) && <Box mt={1}><ProgressBar order={order} /></Box>}
      <Stack direction="row" justifyContent="space-between" mt={0.5}>
        <Typography variant="caption" color="text.secondary">Старт: {fmtDt(order.planned_start)}</Typography>
        <Typography variant="caption" color="text.secondary">Кінець: {fmtDt(order.planned_end)}</Typography>
      </Stack>
      {order.pause_reason && (
        <Typography variant="caption" color="#e65100" display="block" mt={0.3}>Пауза: {order.pause_reason}</Typography>
      )}
    </Paper>
  );
}

const MIXING_STATUS = {
  done: { label: 'Готово', color: '#2e7d32', bg: '#e8f5e9' },
  partial: { label: 'Частково', color: '#e65100', bg: '#fff3e0' },
  pending: { label: 'Очікує', color: '#757575', bg: '#f5f5f5' },
  not_needed: { label: 'Не потрібно', color: '#757575', bg: '#f5f5f5' },
};
const DRYING_STATUS = {
  pending: { label: 'Очікує', color: '#757575' },
  in_progress: { label: 'В процесі', color: '#1565c0' },
  done: { label: 'Завершено', color: '#2e7d32' },
  not_needed: { label: 'Не потрібно', color: '#9e9e9e' },
};
const LOG_ACTIONS = {
  created: 'Створено', started: 'Розпочато', start: 'Розпочато',
  paused: 'Зупинено', pause: 'Зупинено',
  resumed: 'Відновлено', resume: 'Відновлено',
  complete: 'Завершено', completed: 'Завершено',
  setup: 'Наладка', launch: 'Запуск',
  operator_assigned: 'Призначено оператора',
  operator_declined: 'Оператор відмовився',
  quality_check: 'Контроль якості',
  material_added: 'Додано матеріал',
  waste_recorded: 'Записані відходи',
  report: 'Звіт зміни',
  edited: 'Редаговано',
  status_changed: 'Зміна статусу',
  comment_added: 'Коментар',
};

const LOG_DETAIL_KEYS = {
  product: 'Виріб', quantity: 'Кількість', press_form: 'Прес-форма',
  machine: 'Станок', planned_start: 'План старт', planned_end: 'План кінець',
  actual_start: 'Факт старт', actual_end: 'Факт кінець',
  comment: 'Коментар', reason: 'Причина', pause_reason: 'Причина зупинки',
  packaging_note: 'Пакування (нотатка)', packaging_rows: 'Рядки пакування',
  destination_warehouse: 'Склад призначення', setup_at: 'Час наладки',
  launched_at: 'Час запуску', wastes: 'Відходи',
  produced_plan: 'Виготовлено (план)', produced_fact: 'Виготовлено (факт)',
  operator: 'Оператор', status: 'Статус', materials: 'Матеріали',
};

const LOG_DETAIL_VALUES = {
  // pause reasons
  qc_fail: 'Контроль якості не пройдено',
  no_material: 'Немає матеріалу',
  no_operator: 'Немає оператора',
  maintenance: 'Технічне обслуговування',
  breakdown: 'Поломка',
  other: 'Інше',
  // statuses
  planned: 'Заплановано', in_progress: 'В роботі', paused: 'Зупинено',
  completed: 'Завершено', cancelled: 'Скасовано',
};

function SectionTitle({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2, mb: 1 }}>
      {icon && React.cloneElement(icon, { sx: { fontSize: 15, color: '#5c6bc0' } })}
      <Typography variant="overline" sx={{ fontWeight: 700, color: '#5c6bc0', lineHeight: 1 }}>
        {children}
      </Typography>
    </Stack>
  );
}

function InfoRow({ label, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" fontWeight={600} color={color || 'text.primary'} sx={{ textAlign: 'right', maxWidth: '60%' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function ProcessCard({ icon, title, status, statusColor, statusBg, detail }) {
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: statusColor, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" fontWeight={700} display="block">{title}</Typography>
        {detail && <Typography variant="caption" color="text.secondary" noWrap>{detail}</Typography>}
      </Box>
      <Chip label={status} size="small"
        sx={{ fontSize: 10, fontWeight: 700, bgcolor: statusBg, color: statusColor, flexShrink: 0 }} />
    </Paper>
  );
}

const QC_SOURCE_LABELS = { auto: 'Авто', operator: 'Оператор', manager: 'Менеджер' };

function OrderFullDetail({ order, qcChecks }) {
  const total = order.quantity || 0;
  const done = order.produced_fact || 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const waste = order.non_recoverable_waste_kg;

  const mixInfo = order.mixing_batch_info;
  const mixStatus = MIXING_STATUS[mixInfo?.status] || MIXING_STATUS.pending;

  const dryingMats = (order.order_materials || []).filter((m) => m.needs_drying);

  const logs = [...(order.logs || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const packagingRows = order.packaging_rows || [];

  const statusColors = { in_progress: '#2e7d32', paused: '#e65100', planned: '#1565c0', completed: '#424242' };
  const statusLabels = { in_progress: 'В роботі', paused: 'Пауза', planned: 'Заплановано', completed: 'Завершено' };

  return (
    <Box>
      {/* Header info */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5, borderColor: (statusColors[order.status] || '#e0e0e0') + '66' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {order.customer_order_number && (
              <Typography variant="caption" color="#5c6bc0" fontWeight={700}>№ {order.customer_order_number}</Typography>
            )}
            <Typography variant="body1" fontWeight={800} noWrap>{order.product_name}</Typography>
            {order.kit_label && (
              <Typography variant="caption" color="#6a1b9a" fontWeight={600} display="block">{order.kit_label}</Typography>
            )}
            <Typography variant="caption" color="text.secondary">{order.press_form_identifier || '—'}</Typography>
          </Box>
          <Chip label={statusLabels[order.status] || order.status} size="small"
            sx={{ bgcolor: (statusColors[order.status] || '#9e9e9e') + '22', color: statusColors[order.status] || '#9e9e9e', fontWeight: 700 }} />
        </Stack>
        {/* Progress */}
        {total > 0 && (
          <Box mt={1}>
            <Stack direction="row" justifyContent="space-between" mb={0.3}>
              <Typography variant="caption" color="text.secondary">Виготовлено: <b>{done}</b> / {total} шт</Typography>
              <Typography variant="caption" fontWeight={700} color={statusColors[order.status]}>{pct}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 2,
              '& .MuiLinearProgress-bar': { bgcolor: statusColors[order.status] || '#1976d2' } }} />
          </Box>
        )}
        {/* Timeline */}
        <Box mt={1.5}>
          <Stack direction="row" spacing={0} sx={{ position: 'relative' }}>
            {[
              { label: 'План старт', val: fmtDt(order.planned_start), color: '#1565c0' },
              { label: 'Факт старт', val: fmtDt(order.actual_start), color: '#2e7d32' },
              { label: 'План кінець', val: fmtDt(order.planned_end), color: '#6a1b9a' },
              { label: 'Факт кінець', val: fmtDt(order.actual_end), color: '#e65100' },
            ].filter((t) => t.val !== '—').map((t) => (
              <Box key={t.label} sx={{ flex: 1, textAlign: 'center', px: 0.25 }}>
                <Typography sx={{ fontSize: 8.5, color: 'text.secondary', lineHeight: 1.2 }}>{t.label}</Typography>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: t.color, lineHeight: 1.3 }}>{t.val}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Deviation reasons & stats */}
      {(waste != null || order.pause_reason || order.start_late_reason || order.end_late_reason ||
        order.report_reason || order.operator_decline_reason) && (
        <Box sx={{ bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1.5, px: 1.5, py: 0.75, mb: 1.5 }}>
          <InfoRow label="Невідновлювані відходи" value={waste != null ? `${waste} кг` : null} color="#c62828" />
          {order.pause_reason && (
            <Box sx={{ py: 0.3 }}>
              <Typography variant="caption" color="text.secondary">Причина зупинки</Typography>
              <Typography variant="caption" display="block" fontWeight={600} color="#e65100">{order.pause_reason}</Typography>
            </Box>
          )}
          {order.start_late_reason && (
            <Box sx={{ py: 0.3 }}>
              <Typography variant="caption" color="text.secondary">Причина відхилення від планового старту</Typography>
              <Typography variant="caption" display="block" fontWeight={600}>{order.start_late_reason}</Typography>
            </Box>
          )}
          {order.end_late_reason && (
            <Box sx={{ py: 0.3 }}>
              <Typography variant="caption" color="text.secondary">Причина відхилення від планового завершення</Typography>
              <Typography variant="caption" display="block" fontWeight={600}>{order.end_late_reason}</Typography>
            </Box>
          )}
          {order.report_reason && (
            <Box sx={{ py: 0.3 }}>
              <Typography variant="caption" color="text.secondary">Причина (звіт оператора)</Typography>
              <Typography variant="caption" display="block" fontWeight={600}>{order.report_reason}</Typography>
            </Box>
          )}
          {order.operator_decline_reason && (
            <Box sx={{ py: 0.3 }}>
              <Typography variant="caption" color="text.secondary">Причина відмови оператора</Typography>
              <Typography variant="caption" display="block" fontWeight={600} color="#c62828">{order.operator_decline_reason}</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Processes */}
      <SectionTitle icon={<PrecisionManufacturingIcon />}>Процеси</SectionTitle>
      <Stack spacing={0.75}>
        {mixInfo && (
          <ProcessCard
            icon={<BlenderIcon sx={{ fontSize: 18 }} />}
            title="Підготовка замісу"
            status={mixStatus.label}
            statusColor={mixStatus.color}
            statusBg={mixStatus.bg}
            detail={mixInfo.total_added_kg != null
              ? `${mixInfo.total_added_kg} / ${mixInfo.total_required_kg} кг`
              : null}
          />
        )}
        {dryingMats.length > 0 ? dryingMats.map((m) => {
          const ds = DRYING_STATUS[m.drying_status] || DRYING_STATUS.pending;
          return (
            <ProcessCard key={m.id}
              icon={<LocalFireDepartmentIcon sx={{ fontSize: 18 }} />}
              title={`Сушіння: ${m.raw_material_name || m.dye_name}`}
              status={ds.label}
              statusColor={ds.color}
              statusBg={ds.color + '22'}
              detail={m.drying_time_minutes ? `${m.drying_time_minutes} хв` : null}
            />
          );
        }) : (
          <ProcessCard
            icon={<LocalFireDepartmentIcon sx={{ fontSize: 18 }} />}
            title="Сушіння матеріалів"
            status="Н/Д"
            statusColor="#9e9e9e"
            statusBg="#f5f5f5"
          />
        )}
        <ProcessCard
          icon={<PrecisionManufacturingIcon sx={{ fontSize: 18 }} />}
          title="Виготовлення"
          status={statusLabels[order.status] || order.status}
          statusColor={statusColors[order.status] || '#9e9e9e'}
          statusBg={(statusColors[order.status] || '#9e9e9e') + '22'}
          detail={done > 0 ? `${done} шт виготовлено` : null}
        />
      </Stack>

      {/* Materials */}
      {order.order_materials?.length > 0 && (
        <>
          <SectionTitle icon={<ScienceIcon />}>Сировина та барвники</SectionTitle>
          {order.order_materials.map((m) => (
            <Box key={m.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35,
              borderBottom: '1px solid #f0f0f0' }}>
              <Typography variant="caption">{m.raw_material_name || m.dye_name}</Typography>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {Number(m.quantity_required).toLocaleString('uk-UA', { maximumFractionDigits: 3 })} кг
                {m.needs_drying && <Chip label="Сушіння" size="small" sx={{ ml: 0.5, fontSize: 9, height: 16, bgcolor: '#fff3e0', color: '#e65100' }} />}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {/* Packaging */}
      {packagingRows.length > 0 && (
        <>
          <SectionTitle icon={<InventoryIcon />}>Пакування</SectionTitle>
          {packagingRows.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35,
              borderBottom: '1px solid #f0f0f0' }}>
              <Typography variant="caption">{r.name || `Позиція ${i + 1}`}</Typography>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {r.quantity} {r.unit || 'шт'}
                {r.pack_at_machine === false && <Chip label="не на ТПА" size="small" sx={{ ml: 0.5, fontSize: 9, height: 16 }} />}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {/* Logs */}
      {logs.length > 0 && (
        <>
          <SectionTitle icon={<HistoryIcon />}>Журнал подій</SectionTitle>
          <Box sx={{ position: 'relative', pl: 2 }}>
            <Box sx={{ position: 'absolute', left: 7, top: 4, bottom: 4, width: 2, bgcolor: '#e0e0e0', borderRadius: 1 }} />
            {logs.map((l, i) => {
              const performer = l.details?.виконавець || l.details?.adjuster || '';
              return (
                <Box key={i} sx={{ mb: 1, position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: -13, top: 5, width: 8, height: 8,
                    borderRadius: '50%', bgcolor: '#5c6bc0', border: '2px solid white', boxShadow: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#3949ab' }}>
                      {LOG_ACTIONS[l.action] || l.action}
                    </Typography>
                    {performer && (
                      <Typography sx={{ fontSize: 9, color: '#5c6bc0', fontStyle: 'italic' }}>
                        ({performer})
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 9.5, color: 'text.secondary' }}>{fmtDt(l.created_at)}</Typography>
                  {l.details && Object.keys(l.details).length > 0 && (
                    <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, px: 1, py: 0.5, mt: 0.3 }}>
                      {Object.entries(l.details)
                        .filter(([k, v]) => k !== 'виконавець' && k !== 'adjuster' && !['demo'].includes(k) && v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
                        .map(([k, v]) => {
                          const label = LOG_DETAIL_KEYS[k] || k;
                          let display = '';
                          if (Array.isArray(v)) {
                            display = v.join(', ');
                          } else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
                            display = fmtDt(v);
                          } else {
                            display = LOG_DETAIL_VALUES[String(v)] || String(v);
                          }
                          return (
                            <Typography key={k} sx={{ fontSize: 9, color: 'text.secondary' }}>
                              {label}: <span style={{ fontWeight: 600, color: '#424242' }}>{display}</span>
                            </Typography>
                          );
                        })}
                    </Box>
                  )}
                </Box>
              );
            })}
            {/* Shift reports in journal */}
            {(order.operator_shift_reports || []).map((r) => (
              <Box key={`sr-${r.id}`} sx={{ mb: 1, position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: -13, top: 5, width: 8, height: 8,
                  borderRadius: '50%', bgcolor: '#2e7d32', border: '2px solid white', boxShadow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#2e7d32' }}>
                    Звіт оператора
                  </Typography>
                  {r.operator_name && (
                    <Typography sx={{ fontSize: 9, color: '#388e3c', fontStyle: 'italic' }}>
                      ({r.operator_name})
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 9.5, color: 'text.secondary' }}>{fmtDt(r.created_at)}</Typography>
                <Box sx={{ bgcolor: '#f1f8e9', borderRadius: 1, px: 1, py: 0.5, mt: 0.3 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>
                    Виготовлено: <span style={{ fontWeight: 600, color: '#2e7d32' }}>{r.produced_qty} шт</span>
                  </Typography>
                  {r.waste_kg != null && r.waste_kg > 0 && (
                    <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>
                      Відходи: <span style={{ fontWeight: 600 }}>{r.waste_kg} кг</span>
                    </Typography>
                  )}
                  {r.comment && (
                    <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>
                      Коментар: <span style={{ fontWeight: 600 }}>{r.comment}</span>
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Quality checks */}
      {qcChecks && qcChecks.length > 0 && (
        <>
          <SectionTitle icon={<CheckCircleIcon />}>Контроль якості</SectionTitle>
          <Stack spacing={0.75}>
            {qcChecks.map((qc) => (
              <Paper key={qc.id} variant="outlined" sx={{
                p: 1, borderRadius: 1.5,
                borderColor: qc.completed_at ? (qc.passed ? '#2e7d3244' : '#c6282844') : '#1565c044',
                bgcolor: qc.completed_at ? (qc.passed ? '#f1f8e9' : '#fff8f8') : '#e8eaf6',
              }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {qc.completed_at
                    ? (qc.passed
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                      : <CancelIcon sx={{ fontSize: 16, color: '#c62828' }} />)
                    : <CheckCircleIcon sx={{ fontSize: 16, color: '#1565c0' }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: qc.completed_at ? (qc.passed ? '#2e7d32' : '#c62828') : '#1565c0' }}>
                        {qc.completed_at ? (qc.passed ? 'Пройдено' : 'Не пройдено') : 'Очікує'}
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>
                        {QC_SOURCE_LABELS[qc.source] || qc.source}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 9.5, color: 'text.secondary' }}>
                      {fmtDt(qc.completed_at || qc.opened_at)}
                      {qc.operator_name && ` · ${qc.operator_name}`}
                    </Typography>
                    {qc.comment && (
                      <Typography sx={{ fontSize: 9.5, mt: 0.3, fontStyle: 'italic', color: 'text.primary' }}>
                        {qc.comment}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

function MachineDrawer({ machine, orders, onClose }) {
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [qcChecks, setQcChecks] = useState([]);
  const [shiftOperators, setShiftOperators] = useState([]);

  const active = orders.filter((o) => ['in_progress', 'paused'].includes(o.status));
  const current = active[0] || null;
  const queue = orders.filter((o) => o.status === 'planned');

  useEffect(() => {
    if (!machine) return;
    setTab(0);
    setDetail(null);
    setQcChecks([]);
    setShiftOperators([]);
    if (current) {
      setDetailLoading(true);
      productionOrderService.getById(current.id)
        .then((r) => setDetail(r.data))
        .catch(() => setDetail(current))
        .finally(() => setDetailLoading(false));
    }
    qualityCheckService.getAll({ machine: machine.id, page_size: 20 })
      .then((r) => setQcChecks(r.data?.results || r.data || []))
      .catch(() => {});
    staffService.getAll({ shift_machine: machine.id, page_size: 50 })
      .then((r) => setShiftOperators(r.data?.results || r.data || []))
      .catch(() => {});
  }, [machine?.id, current?.id]);

  if (!machine) return null;
  return (
    <Drawer anchor="right" open={!!machine} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a237e', color: '#fff', px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>{machine.identifier}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>{machine.name}</Typography>
            {shiftOperators.length > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5, display: 'block' }}>
                👷 {shiftOperators.map((s) => s.full_name).join(', ')}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff', mt: -0.5 }}><CloseIcon /></IconButton>
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ mt: 1, minHeight: 32,
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)', minHeight: 32, fontSize: 12, py: 0.5, px: 1.5, textTransform: 'none' },
            '& .Mui-selected': { color: '#fff', fontWeight: 700 },
            '& .MuiTabs-indicator': { bgcolor: '#fff' },
          }}>
          <Tab label={`Поточне${current ? '' : ' (—)'}`} />
          <Tab label={`Черга (${queue.length})`} />
          <Tab label={`Оператори (${shiftOperators.length})`} />
        </Tabs>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {tab === 0 && (
          detailLoading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
            : (detail || current)
              ? <OrderFullDetail order={detail || current} qcChecks={qcChecks} />
              : <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center', fontStyle: 'italic' }}>
                  Немає активного замовлення
                </Typography>
        )}
        {tab === 1 && (
          queue.length === 0
            ? <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center', fontStyle: 'italic' }}>Черга порожня</Typography>
            : queue.map((o, i) => (
                <Paper key={o.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="#5c6bc0" fontWeight={700}>
                        #{i + 1} {o.customer_order_number ? `· № ${o.customer_order_number}` : ''}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} noWrap>{o.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.press_form_identifier || '—'}</Typography>
                    </Box>
                    <Chip label="Заплановано" size="small"
                      sx={{ fontSize: 10, fontWeight: 700, bgcolor: '#e8eaf6', color: '#1565c0' }} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" mt={0.75}>
                    <Typography variant="caption" color="text.secondary">Кількість: <b>{o.quantity} шт</b></Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDt(o.planned_start)}</Typography>
                  </Stack>
                  <InfoRow label="Кінець" value={fmtDt(o.planned_end)} />
                </Paper>
              ))
        )}
        {tab === 2 && (
          shiftOperators.length === 0
            ? <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Жоден оператор не вибрав цей ТПА для поточної зміни
                </Typography>
              </Box>
            : <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Оператори, що обрали цей ТПА для поточної зміни:
                </Typography>
                {shiftOperators.map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#e8eaf6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 700, color: '#3949ab', flexShrink: 0 }}>
                      {s.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>{s.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.role_name || s.role?.name || '—'}</Typography>
                    </Box>
                    <Chip label="Зараз тут" size="small" sx={{ fontSize: 10, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                  </Paper>
                ))}
              </>
        )}
      </Box>
    </Drawer>
  );
}

function OrderDialog({ order, onClose, onEdit }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qcChecks, setQcChecks] = useState([]);

  useEffect(() => {
    if (!order) return;
    setDetail(null);
    setQcChecks([]);
    setLoading(true);
    Promise.all([
      productionOrderService.getById(order.id),
      order.machine ? qualityCheckService.getAll({ machine: order.machine, page_size: 20 }) : Promise.resolve({ data: [] }),
    ]).then(([r, qr]) => {
      setDetail(r.data);
      setQcChecks(qr.data?.results || qr.data || []);
    }).catch(() => setDetail(order))
      .finally(() => setLoading(false));
  }, [order?.id]);

  if (!order) return null;
  const shown = detail || order;
  const canEdit = !['completed', 'cancelled'].includes(shown.status);
  return (
    <Dialog open={!!order} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          {shown.customer_order_number && (
            <Typography variant="caption" color="#5c6bc0" fontWeight={700}>№ {shown.customer_order_number}</Typography>
          )}
          <Typography variant="h6" fontWeight={800} noWrap>{shown.product_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {shown.machine_identifier} · {shown.press_form_identifier || '—'}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(shown)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: 1.5 }}>
              Редагувати
            </Button>
          )}
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          : <OrderFullDetail order={shown} qcChecks={qcChecks} />}
      </DialogContent>
    </Dialog>
  );
}

const toLocalDT = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function ScheduleDialog({ coLine, machines, products, orders, defaultMachineId, onClose, onSave }) {
  const compatibleMachines = useMemo(() => {
    const product = products.find((p) => p.id === coLine?.product);
    if (!product?.manufacturing_configs?.length) return machines;
    const ids = new Set(product.manufacturing_configs.map((c) => c.machine));
    return machines.filter((m) => ids.has(m.id));
  }, [machines, products, coLine]);

  const calcStart = useCallback((mId) => {
    const ends = orders
      .filter((o) => o.machine === mId && ['planned', 'in_progress', 'paused'].includes(o.status) && o.planned_end)
      .map((o) => new Date(o.planned_end).getTime());
    return toLocalDT(ends.length ? new Date(Math.max(...ends)) : new Date());
  }, [orders]);

  const [machineId, setMachineId] = useState(() => defaultMachineId || compatibleMachines[0]?.id || '');
  const [plannedStart, setPlannedStart] = useState(() => calcStart(defaultMachineId || compatibleMachines[0]?.id || null));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleMachineChange = (newId) => {
    setMachineId(newId);
    setPlannedStart(calcStart(newId));
  };

  const handleSave = async () => {
    if (!machineId || !plannedStart) { setErr('Оберіть станок і час старту'); return; }
    setSaving(true); setErr('');
    try {
      if (coLine.production_order) {
        await productionOrderService.update(coLine.production_order, {
          machine: machineId,
          planned_start: new Date(plannedStart).toISOString(),
          status: 'planned',
        });
      } else {
        const product = products.find((p) => p.id === coLine.product);
        const config = product?.manufacturing_configs?.find((c) => c.machine === machineId);
        await productionOrderService.create({
          product: coLine.product,
          quantity: coLine.quantity,
          machine: machineId,
          press_form: config?.press_form,
          planned_start: new Date(plannedStart).toISOString(),
          customer_order_line: coLine.id,
          destination_warehouse: 'Склад готової продукції',
        });
      }
      onSave(); onClose();
    } catch (e) {
      const d = e?.response?.data;
      const msg = typeof d === 'string' ? d
        : d?.detail || Object.entries(d || {}).map(([k, v]) => `${k}: ${v}`).join('; ');
      setErr(msg || 'Помилка збереження');
    } finally { setSaving(false); }
  };

  if (!coLine) return null;
  return (
    <Dialog open={!!coLine} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={800}>Запланувати</Typography>
            <Typography variant="caption" color="text.secondary">
              {coLine.product_name} · {coLine.quantity} шт
            </Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">Замовлення</Typography>
            <Typography variant="body2" fontWeight={600}>№ {coLine.orderNumber} · {coLine.customer_name}</Typography>
          </Box>
          <TextField select label="Станок" value={machineId} onChange={(e) => handleMachineChange(e.target.value)} fullWidth>
            {compatibleMachines.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.identifier} — {m.name}</MenuItem>
            ))}
          </TextField>
          <TextField label="Час старту" type="datetime-local" value={plannedStart}
            onChange={(e) => setPlannedStart(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          {err && <Typography color="error" variant="caption">{err}</Typography>}
        </Stack>
      </DialogContent>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>Скасувати</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Запланувати'}
        </Button>
      </Box>
    </Dialog>
  );
}

function UnscheduledDialog({ coLine, onClose, onNavigate }) {
  if (!coLine) return null;
  return (
    <Dialog open={!!coLine} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={800}>{coLine.product_name}</Typography>
            <Typography variant="caption" color="#5c6bc0">№ {coLine.orderNumber}</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5}>
          {[
            ['Замовник', coLine.customer_name],
            ['Кількість', `${coLine.quantity} шт`],
            ['Дедлайн', coLine.deadline ? fmtDate(coLine.deadline) : '—'],
            ['Ціна', coLine.price ? `${coLine.price} грн` : '—'],
          ].map(([label, value]) => (
            <Grid item xs={6} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight={600}>{value}</Typography>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<CalendarMonthIcon />}
          onClick={() => onNavigate(coLine)}
          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
          Запланувати
        </Button>
      </Box>
    </Dialog>
  );
}

// ─── List view ────────────────────────────────────────────────────────────────

const SORT_FIELDS = [
  { value: 'planned_start', label: 'Час планування' },
  { value: 'planned_end', label: 'Кінець плану' },
  { value: 'created_at', label: 'Дата створення' },
  { value: 'customer_order_number', label: 'Номер замовлення' },
  { value: 'product_name', label: 'Назва виробу' },
  { value: 'machine_identifier', label: 'Станок' },
  { value: 'quantity', label: 'Кількість' },
  { value: 'produced_fact', label: 'Виготовлено' },
];

function ListSortLabel({ field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <TableSortLabel active={active} direction={active ? sortDir : 'asc'} onClick={() => onSort(field)}>
      {SORT_FIELDS.find((f) => f.value === field)?.label || field}
    </TableSortLabel>
  );
}

function ListView({ orders, machines, onOrderClick }) {
  const [filters, setFilters] = useState({ search: '', status: '', machine: '', dateFrom: '', dateTo: '' });
  const [sortField, setSortField] = useState('planned_start');
  const [sortDir, setSortDir] = useState('asc');

  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let items = [...orders];
    const q = filters.search.toLowerCase();
    if (q) items = items.filter((o) =>
      (o.product_name || '').toLowerCase().includes(q) ||
      (o.customer_order_number || '').toLowerCase().includes(q) ||
      (o.machine_identifier || '').toLowerCase().includes(q) ||
      (o.press_form_identifier || '').toLowerCase().includes(q)
    );
    if (filters.status) items = items.filter((o) => o.status === filters.status);
    if (filters.machine) items = items.filter((o) => String(o.machine) === filters.machine);
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      items = items.filter((o) => o.planned_start && new Date(o.planned_start) >= from);
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      items = items.filter((o) => o.planned_start && new Date(o.planned_start) <= to);
    }
    items.sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), 'uk');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [orders, filters, sortField, sortDir]);

  return (
    <Box>
      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <FilterListIcon sx={{ color: 'text.secondary' }} />
          <TextField size="small" placeholder="Пошук…" value={filters.search}
            onChange={(e) => setF('search', e.target.value)} sx={{ width: 180 }} />
          <TextField select size="small" label="Статус" value={filters.status}
            onChange={(e) => setF('status', e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="">Всі статуси</MenuItem>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Станок" value={filters.machine}
            onChange={(e) => setF('machine', e.target.value)} sx={{ width: 160 }}>
            <MenuItem value="">Всі станки</MenuItem>
            {machines.map((m) => <MenuItem key={m.id} value={String(m.id)}>{m.identifier}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Старт від" type="date" value={filters.dateFrom}
            onChange={(e) => setF('dateFrom', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 145 }} />
          <TextField size="small" label="Старт до" type="date" value={filters.dateTo}
            onChange={(e) => setF('dateTo', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 145 }} />
          {(filters.search || filters.status || filters.machine || filters.dateFrom || filters.dateTo) && (
            <Button size="small" onClick={() => setFilters({ search: '', status: '', machine: '', dateFrom: '', dateTo: '' })}>
              Скинути
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {filtered.length} записів
          </Typography>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><ListSortLabel field="customer_order_number" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell><ListSortLabel field="product_name" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell><ListSortLabel field="machine_identifier" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell>Прес-форма</TableCell>
              <TableCell><ListSortLabel field="quantity" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell><ListSortLabel field="produced_fact" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell><ListSortLabel field="planned_start" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell><ListSortLabel field="planned_end" {...{ sortField, sortDir, onSort: handleSort }} /></TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Прогрес</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((o) => (
              <TableRow key={o.id} hover onClick={() => onOrderClick(o)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                  <Typography variant="body2" color="#5c6bc0" fontWeight={600}>
                    {o.customer_order_number || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                    {o.product_name}
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="body2">{o.machine_identifier}</Typography></TableCell>
                <TableCell><Typography variant="body2">{o.press_form_identifier || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{o.quantity}</Typography></TableCell>
                <TableCell><Typography variant="body2">{o.produced_fact || 0}</Typography></TableCell>
                <TableCell><Typography variant="body2" noWrap>{fmtDt(o.planned_start)}</Typography></TableCell>
                <TableCell><Typography variant="body2" noWrap>{fmtDt(o.planned_end)}</Typography></TableCell>
                <TableCell><StatusChip status={o.status} /></TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  {o.quantity > 0 && (
                    <LinearProgress variant="determinate"
                      value={Math.min(100, Math.round(((o.produced_fact || 0) / o.quantity) * 100))}
                      sx={{ borderRadius: 2, height: 5 }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Нічого не знайдено
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Grid view (рядки = ТПА, колонки = черга / незаплановані) ────────────────

function CompactMachineTile({ machine, orders, onClick }) {
  const active = orders.filter((o) => ['in_progress', 'paused', 'planned'].includes(o.status));
  const current = active.find((o) => o.status === 'in_progress')
    || active.find((o) => o.status === 'paused')
    || active[0];
  const dotColor = current
    ? ({ in_progress: '#2e7d32', paused: '#e65100', planned: '#1565c0' }[current.status] || '#9e9e9e')
    : '#bdbdbd';
  const pct = current && current.quantity > 0
    ? Math.min(100, Math.round(((current.produced_fact || 0) / current.quantity) * 100))
    : 0;

  return (
    <Box onClick={() => onClick(machine, active)} sx={{
      px: 1, py: 0.5, cursor: 'pointer', height: '100%',
      borderRight: '1px solid #e0e0e0', display: 'flex',
      flexDirection: 'column', justifyContent: 'center',
      '&:hover': { bgcolor: '#f0f4ff' }, transition: 'background 0.1s',
    }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <FiberManualRecordIcon sx={{ fontSize: 9, color: dotColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1.2 }} noWrap>
          {machine.identifier}
        </Typography>
      </Stack>
      {current ? (
        <>
          <Typography sx={{ fontSize: 9.5, color: 'text.secondary', lineHeight: 1.2 }} noWrap>
            {current.product_name}
          </Typography>
          {['in_progress', 'paused'].includes(current.status) && (
            <LinearProgress variant="determinate" value={pct}
              sx={{ height: 3, borderRadius: 1, mt: 0.3,
                '& .MuiLinearProgress-bar': { bgcolor: dotColor } }} />
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: 9.5, color: 'text.disabled', fontStyle: 'italic' }}>Вільний</Typography>
      )}
    </Box>
  );
}

function CompactOrderTile({ order, onClick }) {
  const cfg = STATUS_CFG[order.status] || { label: order.status, bg: '#eee', color: '#555' };
  return (
    <Box onClick={(e) => { e.stopPropagation(); onClick(order); }} sx={{
      width: 114, flexShrink: 0, cursor: 'pointer',
      bgcolor: cfg.bg, border: `1px solid ${cfg.color}44`,
      borderRadius: 1.5, px: 0.75, py: 0.4,
      '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
      transition: 'all 0.1s',
    }}>
      {order.customer_order_number && (
        <Typography sx={{ fontSize: 8.5, color: '#5c6bc0', fontWeight: 700, lineHeight: 1.2, display: 'block' }} noWrap>
          №{order.customer_order_number}
        </Typography>
      )}
      <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1.3, display: 'block' }} noWrap>
        {order.product_name}
      </Typography>
      {order.kit_label && (
        <Typography sx={{ fontSize: 8, color: '#6a1b9a', fontWeight: 600, lineHeight: 1.2, display: 'block' }} noWrap title={order.kit_label}>
          {order.kit_label}
        </Typography>
      )}
      <Typography sx={{ fontSize: 9, color: cfg.color, fontWeight: 700 }}>{cfg.label}</Typography>
      <Typography sx={{ fontSize: 9, color: 'text.secondary', display: 'block' }} noWrap>
        {fmtDt(order.planned_start)}
      </Typography>
    </Box>
  );
}

function CompactUnscheduledTile({ coLine, onClick }) {
  return (
    <Box onClick={(e) => { e.stopPropagation(); onClick(coLine); }} sx={{
      width: 114, flexShrink: 0, cursor: 'pointer',
      bgcolor: '#fff8e1', border: '1px solid #f9a82555',
      borderRadius: 1.5, px: 0.75, py: 0.4,
      '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
      transition: 'all 0.1s',
    }}>
      <Typography sx={{ fontSize: 8.5, color: '#f57f17', fontWeight: 700, lineHeight: 1.2, display: 'block' }}>
        №{coLine.orderNumber}
      </Typography>
      <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1.3, display: 'block' }} noWrap>
        {coLine.product_name}
      </Typography>
      {coLine.deadline && (
        <Typography sx={{ fontSize: 9, color: 'text.secondary', display: 'block' }}>до {fmtDate(coLine.deadline)}</Typography>
      )}
    </Box>
  );
}

function GridView({ machines, orders, allOrders, products, customerOrders, onMachineClick, onQueueClick, onUnscheduledClick }) {
  const ordersByMachine = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (!map[o.machine]) map[o.machine] = [];
      map[o.machine].push(o);
    }
    return map;
  }, [orders]);

  const queueByMachine = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (!['planned', 'in_progress', 'paused'].includes(o.status)) continue;
      if (!map[o.machine]) map[o.machine] = [];
      map[o.machine].push(o);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const pri = { in_progress: 0, paused: 1, planned: 2 };
        const d = (pri[a.status] ?? 3) - (pri[b.status] ?? 3);
        return d !== 0 ? d : new Date(a.planned_start) - new Date(b.planned_start);
      });
    }
    return map;
  }, [orders]);

  // machine_id -> Set<product_id> — з manufacturing_configs виробів
  const machineProductSet = useMemo(() => {
    const map = {};
    for (const p of products) {
      for (const cfg of (p.manufacturing_configs || [])) {
        if (!cfg.machine) continue;
        if (!map[cfg.machine]) map[cfg.machine] = new Set();
        map[cfg.machine].add(p.id);
      }
    }
    return map;
  }, [products]);

  const activeOrderIds = useMemo(() =>
    new Set(orders.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status)).map((o) => o.id)),
    [orders]);

  const unscheduledLines = useMemo(() => {
    const lines = [];
    for (const co of customerOrders) {
      for (const line of (co.lines || [])) {
        // Рядок покритий супутнім виробництвом комплекту форми — не показувати окремо
        if (line.co_produced_by_order) continue;
        if (!line.production_order || !activeOrderIds.has(line.production_order)) {
          if (['completed', 'cancelled'].includes(line.production_status)) continue;
          lines.push({ ...line, orderNumber: co.number, customer_name: co.customer_name, deadline: co.deadline, co_id: co.id });
        }
      }
    }
    return lines;
  }, [customerOrders, activeOrderIds]);

  const machinesSorted = useMemo(() => [...machines].sort((a, b) => {
    const aA = (ordersByMachine[a.id] || []).some((o) => o.status === 'in_progress');
    const bA = (ordersByMachine[b.id] || []).some((o) => o.status === 'in_progress');
    return (bA ? 1 : 0) - (aA ? 1 : 0);
  }), [machines, ordersByMachine]);

  const colScrollSx = {
    display: 'flex', alignItems: 'center', gap: 0.5,
    px: 0.75, py: 0.5, overflowX: 'auto',
    '&::-webkit-scrollbar': { height: 3 },
    '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 },
  };
  const hdrSx = {
    px: 1, py: 0.5, fontSize: 10, fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: 0.5,
    color: 'text.secondary', bgcolor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
    display: 'flex', alignItems: 'center', gap: 0.5,
  };
  const COLS = '158px 1fr 1fr';

  const queueTotal = orders.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status)).length;

  return (
    <Box sx={{ borderTop: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: COLS }}>
        <Box sx={{ ...hdrSx, borderRight: '1px solid #e0e0e0' }}>
          <MemoryIcon sx={{ fontSize: 12 }} />
          Станок ({machinesSorted.length})
        </Box>
        <Box sx={{ ...hdrSx, borderRight: '1px solid #e0e0e0' }}>
          <QueueIcon sx={{ fontSize: 12 }} />
          Черга ({queueTotal})
        </Box>
        <Box sx={hdrSx}>
          <AssignmentIcon sx={{ fontSize: 12 }} />
          Не заплановані ({unscheduledLines.length})
        </Box>
      </Box>

      {/* Rows */}
      {machinesSorted.map((machine, idx) => {
        const queue = queueByMachine[machine.id] || [];
        return (
          <Box key={machine.id} sx={{
            display: 'grid', gridTemplateColumns: COLS,
            minHeight: 54,
            borderBottom: idx < machinesSorted.length - 1 ? '1px solid #eeeeee' : 'none',
          }}>
            <CompactMachineTile
              machine={machine}
              orders={ordersByMachine[machine.id] || []}
              onClick={onMachineClick}
            />
            <Box sx={{ ...colScrollSx, borderRight: '1px solid #e0e0e0' }}>
              {queue.length === 0
                ? <Typography sx={{ fontSize: 10, color: 'text.disabled', fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</Typography>
                : queue.map((o) => <CompactOrderTile key={o.id} order={o} onClick={onQueueClick} />)}
            </Box>
            <Box sx={colScrollSx}>
              {(() => {
                const compatible = unscheduledLines.filter((line) => {
                  const products = machineProductSet[machine.id];
                  return products ? products.has(line.product) : false;
                });
                return compatible.length === 0
                  ? <Typography sx={{ fontSize: 10, color: 'text.disabled', fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</Typography>
                  : compatible.map((line) => (
                    <CompactUnscheduledTile
                      key={`${line.co_id}-${line.id}`}
                      coLine={line}
                      onClick={(l) => onUnscheduledClick(l, machine)}
                    />
                  ));
              })()}
            </Box>
          </Box>
        );
      })}

      {machinesSorted.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Немає станків</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlannerV2Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [machines, setMachines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [shiftReportsDialog, setShiftReportsDialog] = useState(null); // { loading, rows, error }

  const [machineDrawer, setMachineDrawer] = useState(null);
  const [queueDialog, setQueueDialog] = useState(null);
  const [unscheduledDialog, setUnscheduledDialog] = useState(null);
  const [listOrderDialog, setListOrderDialog] = useState(null);
  const [planDialog, setPlanDialog] = useState(null); // { coLine }
  const [unassignedCount, setUnassignedCount] = useState(0);

  // ── Materials needed ────────────────────────────────────────────────────────
  const [matNeedOpen, setMatNeedOpen] = useState(false);
  const [matNeedData, setMatNeedData] = useState([]);
  const [matNeedLoading, setMatNeedLoading] = useState(false);
  const [matNeedViewMode, setMatNeedViewMode] = useState('aggregated'); // 'aggregated' | 'by_order'
  const [matNeedDateFrom, setMatNeedDateFrom] = useState('');
  const [matNeedDateTo, setMatNeedDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  });
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState([]); // active requests for display
  const [noMaterialCount, setNoMaterialCount] = useState(0);
  const [matReqDialog, setMatReqDialog] = useState(null); // { type, material_id, name, unit, shortage }
  const [matReqQty, setMatReqQty] = useState('');
  const [matReqNote, setMatReqNote] = useState('');
  const [matReqSaving, setMatReqSaving] = useState(false);

  const loadPendingRequestsCount = useCallback(async () => {
    try {
      const [reqRes, warnRes] = await Promise.all([
        api.get('/stock-material-requests/?status=new&page_size=1000'),
        api.get('/production-orders/shortage-warning-count/'),
      ]);
      const items = reqRes.data?.results ?? reqRes.data ?? [];
      setPendingRequestsCount(Array.isArray(items) ? items.length : 0);
      setPendingRequests(Array.isArray(items) ? items : []);
      setNoMaterialCount(warnRes.data?.count ?? 0);
    } catch {}
  }, []);

  // Build lookup: 'raw_material:5' -> request object
  const pendingReqByKey = useMemo(() => {
    const map = {};
    pendingRequests.forEach((r) => {
      const key = r.raw_material ? `raw_material:${r.raw_material}`
        : r.dye ? `dye:${r.dye}`
        : r.packaging_material ? `packaging:${r.packaging_material}`
        : null;
      if (key) {
        if (!map[key]) map[key] = { quantity: 0, created_at: r.created_at };
        map[key].quantity += r.quantity - (r.transferred_quantity || 0);
        if (r.created_at < map[key].created_at) map[key].created_at = r.created_at;
      }
    });
    return map;
  }, [pendingRequests]);

  const loadMatNeed = useCallback(async () => {
    setMatNeedLoading(true);
    try {
      const params = {};
      if (matNeedDateFrom) params.date_from = matNeedDateFrom;
      if (matNeedDateTo) params.date_to = matNeedDateTo;
      const res = await api.get('/production-orders/materials-needed/', { params });
      setMatNeedData(res.data ?? []);
    } catch {
      setMatNeedData([]);
    } finally {
      setMatNeedLoading(false);
    }
  }, [matNeedDateFrom, matNeedDateTo]);

  useEffect(() => {
    if (matNeedOpen) loadMatNeed();
  }, [matNeedOpen, loadMatNeed]);

  useEffect(() => { loadPendingRequestsCount(); }, [loadPendingRequestsCount]);

  // Deep-link зі сповіщення «Звіт за зміну»: ?shiftReports=1,2,3
  useEffect(() => {
    const raw = searchParams.get('shiftReports');
    if (!raw) return undefined;
    const ids = raw.split(',').map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
    if (!ids.length) return undefined;
    let cancelled = false;
    setShiftReportsDialog({ loading: true, rows: [], error: '' });
    (async () => {
      try {
        const res = await operatorShiftReportService.getAll({ page_size: 200, ordering: '-created_at' });
        const all = res.data?.results || res.data || [];
        const idSet = new Set(ids);
        const rows = (Array.isArray(all) ? all : []).filter((r) => idSet.has(Number(r.id)));
        if (!cancelled) setShiftReportsDialog({ loading: false, rows, error: rows.length ? '' : 'Звіти не знайдено' });
      } catch {
        if (!cancelled) setShiftReportsDialog({ loading: false, rows: [], error: 'Не вдалося завантажити звіти' });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  const closeShiftReportsDialog = () => {
    setShiftReportsDialog(null);
    if (searchParams.has('shiftReports')) {
      const next = new URLSearchParams(searchParams);
      next.delete('shiftReports');
      setSearchParams(next, { replace: true });
    }
  };

  const handleCreateMatRequest = async () => {
    if (!matReqDialog) return;
    const qty = parseFloat(matReqQty);
    if (!qty || qty <= 0) return;
    setMatReqSaving(true);
    try {
      let requester = 'Планер';
      try {
        const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
        requester = u?.full_name || u?.username || 'Планер';
      } catch { /* keep default */ }
      const payload = { quantity: qty, notes: matReqNote, requester_name: requester, item_category: matReqDialog.type };
      if (matReqDialog.type === 'raw_material') payload.raw_material = matReqDialog.material_id;
      else if (matReqDialog.type === 'dye') payload.dye = matReqDialog.material_id;
      else if (matReqDialog.type === 'packaging' || matReqDialog.type === 'packaging_material') {
        payload.packaging_material = matReqDialog.material_id;
        payload.item_category = 'packaging_material';
      }
      await api.post('/stock-material-requests/', payload);
      setMatReqDialog(null); setMatReqQty(''); setMatReqNote('');
      await loadPendingRequestsCount();
    } catch {}
    setMatReqSaving(false);
  };

  // ── Downtime report ─────────────────────────────────────────────────────────
  const [downtimeDialogOpen, setDowntimeDialogOpen] = useState(false);
  const [downtimeData, setDowntimeData] = useState(null);
  const [downtimeLoading, setDowntimeLoading] = useState(false);
  const [downtimeDateFrom, setDowntimeDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 16);
  });
  const [downtimeDateTo, setDowntimeDateTo] = useState(() => new Date().toISOString().slice(0, 16));
  const [downtimeMachines, setDowntimeMachines] = useState([]);

  const loadDowntimeReport = useCallback(async () => {
    setDowntimeLoading(true);
    try {
      const params = {
        date_from: downtimeDateFrom,
        date_to: downtimeDateTo,
      };
      if (downtimeMachines.length > 0) params.machines = downtimeMachines.join(',');
      const r = await getDowntimeReport(params);
      setDowntimeData(r.data);
    } catch (e) { console.error(e); }
    finally { setDowntimeLoading(false); }
  }, [downtimeDateFrom, downtimeDateTo, downtimeMachines]);

  useEffect(() => {
    if (downtimeDialogOpen) loadDowntimeReport();
  }, [downtimeDialogOpen, loadDowntimeReport]);

  // ── Maintenance alerts ───────────────────────────────────────────────────────
  const [maintTickets, setMaintTickets] = useState([]);
  const [maintDialogOpen, setMaintDialogOpen] = useState(false);
  const [maintCommentDialog, setMaintCommentDialog] = useState(null); // { ticket, act }
  const [maintCommentText, setMaintCommentText] = useState('');
  const [maintCommentScheduledAt, setMaintCommentScheduledAt] = useState('');
  const [maintCommentSaving, setMaintCommentSaving] = useState(false);

  const loadMaintTickets = useCallback(async () => {
    try {
      await checkMaintenanceAlerts();
    } catch {}
    try {
      const r = await maintenanceTicketService.getAll();
      const all = Array.isArray(r.data) ? r.data : r.data?.results || [];
      setMaintTickets(all.filter((t) => t.status !== 'done'));
    } catch {}
  }, []);

  useEffect(() => {
    loadMaintTickets();
    const id = setInterval(loadMaintTickets, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadMaintTickets]);

  const handleMaintAction = (ticket, act) => {
    if (act.needsComment || act.withSchedule) {
      setMaintCommentText('');
      setMaintCommentScheduledAt('');
      setMaintCommentDialog({ ticket, act });
    } else {
      applyMaintAction(ticket, act, '', '');
    }
  };

  const applyMaintAction = async (ticket, act, comment, scheduledAt) => {
    const newStatus = act.key === 'continue_production_mt' ? 'continue_production' : act.key;
    try {
      const patch = { status: newStatus };
      if (scheduledAt) patch.scheduled_at = scheduledAt;
      await maintenanceTicketService.patch(ticket.id, patch);
      if (comment.trim()) {
        await maintenanceTicketService.addComment(ticket.id, {
          text: comment.trim(),
          author_type: 'manager',
          action_label: act.label,
        });
      }
      await loadMaintTickets();
    } catch (e) { console.error(e); }
  };

  const handleMaintCommentSubmit = async () => {
    const { act } = maintCommentDialog;
    if (act.needsComment && !maintCommentText.trim()) return;
    if (act.withSchedule && !maintCommentScheduledAt) return;
    setMaintCommentSaving(true);
    try {
      await applyMaintAction(maintCommentDialog.ticket, act, maintCommentText, maintCommentScheduledAt);
      setMaintCommentDialog(null);
    } finally { setMaintCommentSaving(false); }
  };

  // ── QC trigger ──────────────────────────────────────────────────────────────
  const [qcOpen, setQcOpen] = useState(false);
  const [qcMachines, setQcMachines] = useState([]);
  const [qcOperator, setQcOperator] = useState('');
  const [qcStaff, setQcStaff] = useState([]);
  const [qcSubmitting, setQcSubmitting] = useState(false);
  const [qcSuccess, setQcSuccess] = useState(false);

  const openQc = async () => {
    setQcMachines([]); setQcOperator(''); setQcSuccess(false);
    staffService.getAll({ page_size: 500 })
      .then((r) => setQcStaff(r.data?.results || r.data || [])).catch(() => {});
    setQcOpen(true);
  };
  const submitQc = async () => {
    if (!qcMachines.length) return;
    setQcSubmitting(true);
    try {
      const responses = await Promise.all(qcMachines.map((mId) =>
        qualityCheckService.trigger({
          machines: [mId], operator: qcOperator || undefined,
          triggered_by: undefined, source: 'manager',
        })
      ));
      const linked = responses.flatMap((r) => r?.data?.order_quality_check_orders || []);
      if (!linked.length) {
        alert(
          'Перевірку ТПА створено, але в канбані ВКЯ картки немає: на обраних станках немає запущеного замовлення. '
          + 'Оберіть ТПА, де зараз є ЗВ «в роботі» (зараз: ТПА з активним замовленням).'
        );
        setQcOpen(false);
        return;
      }
      setQcSuccess(true);
      setTimeout(() => setQcOpen(false), 1200);
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка');
    } finally { setQcSubmitting(false); }
  };

  // ── Add break ───────────────────────────────────────────────────────────────
  const nowIso = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const [breakOpen, setBreakOpen] = useState(false);
  const [breakForm, setBreakForm] = useState({ machine: '', start_at: '', end_at: '', reason: '' });
  const [breakSaving, setBreakSaving] = useState(false);
  const [breakErr, setBreakErr] = useState('');

  const openBreak = () => {
    const n = nowIso(); const n1 = nowIso(); // auto-fill +1h
    const d1 = new Date(); d1.setHours(d1.getHours() + 1);
    const pad = (x) => String(x).padStart(2, '0');
    const end = `${d1.getFullYear()}-${pad(d1.getMonth()+1)}-${pad(d1.getDate())}T${pad(d1.getHours())}:${pad(d1.getMinutes())}`;
    setBreakForm({ machine: '', start_at: n, end_at: end, reason: '' });
    setBreakErr('');
    setBreakOpen(true);
  };
  const submitBreak = async () => {
    if (!breakForm.machine || !breakForm.start_at || !breakForm.end_at || !breakForm.reason.trim()) {
      setBreakErr('Заповніть всі поля'); return;
    }
    setBreakSaving(true); setBreakErr('');
    try {
      await machineBreakService.create({
        machine: Number(breakForm.machine),
        start_at: breakForm.start_at.length === 16 ? `${breakForm.start_at}:00` : breakForm.start_at,
        end_at: breakForm.end_at.length === 16 ? `${breakForm.end_at}:00` : breakForm.end_at,
        reason: breakForm.reason,
      });
      setBreakOpen(false);
      refresh();
    } catch (e) {
      setBreakErr(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join('; ') || 'Помилка');
    } finally { setBreakSaving(false); }
  };

  // ── Machine operators ───────────────────────────────────────────────────────
  // ── Shift assignment ──────────────────────────────────────────────────
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftList, setShiftList] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shiftStaff, setShiftStaff] = useState([]); // all staff
  const [shiftAssign, setShiftAssign] = useState({}); // machineId → operatorId
  const [shiftSaving, setShiftSaving] = useState(false);
  const [shiftLoading, setShiftLoading] = useState(false);

  const openShift = () => {
    // Open immediately — full /staff/ with machines_data was ~0.5MB and blocked UI ~6s
    setSelectedShiftId('');
    setShiftAssign({});
    setShiftLoading(true);
    setShiftOpen(true);
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      workShiftService.getAll({ page_size: 100 }),
      staffService.getAll({ page_size: 500, shift_picker: 1 }),
      dailyMachineOperatorService.getAll({ date: today, page_size: 500 }).catch(() => ({ data: [] })),
    ])
      .then(([shiftsRes, staffRes, existRes]) => {
        const shifts = (shiftsRes.data?.results || shiftsRes.data || []).filter((s) => s.is_active);
        const staff = (staffRes.data?.results || staffRes.data || []).filter((s) => s.is_active);
        setShiftList(shifts);
        setShiftStaff(staff);
        const existing = existRes.data?.results || existRes.data || [];
        const initAssign = {};
        existing.forEach((a) => { initAssign[a.machine] = a.operator || ''; });
        setShiftAssign(initAssign);
        if (existing.length > 0 && existing[0].shift) setSelectedShiftId(String(existing[0].shift));
        else if (shifts.length > 0) setSelectedShiftId(String(shifts[0].id));
      })
      .catch(() => {})
      .finally(() => setShiftLoading(false));
  };

  const saveShift = async () => {
    if (!selectedShiftId) { alert('Оберіть зміну'); return; }
    setShiftSaving(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      const assignments = machines.map((m) => ({
        machine: m.id,
        operator: shiftAssign[m.id] || null,
      }));
      await dailyMachineOperatorService.bulkSave({
        date: today,
        shift: Number(selectedShiftId),
        assignments,
      });
      setShiftOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка збереження');
    } finally { setShiftSaving(false); }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    productionOrderService.unassignedOperatorCount()
      .then((r) => setUnassignedCount(r.data?.count || 0)).catch(() => {});
    try {
      const [m, o, co, pr] = await Promise.all([
        machineService.getAll(),
        productionOrderService.getAll({ page_size: 500 }),
        customerOrderService.getAll({ page_size: 500 }),
        productService.getAll(),
      ]);
      setMachines(Array.isArray(m.data) ? m.data : (m.data?.results || []));
      setOrders(Array.isArray(o.data) ? o.data : (o.data?.results || []));
      setCustomerOrders(Array.isArray(co.data) ? co.data : (co.data?.results || []));
      setProducts(Array.isArray(pr.data) ? pr.data : (pr.data?.results || []));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // For list view, show all non-cancelled orders
  const listOrders = useMemo(() =>
    orders.filter((o) => o.status !== 'cancelled'),
    [orders]
  );

  // For kanban, only active
  const activeOrders = useMemo(() =>
    orders.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status)),
    [orders]
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 200,
        bgcolor: 'white', px: 2, pt: 1, pb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        {/* Перемикач режимів */}
        <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: 1.5, p: 0.4, gap: 0.5 }}>
          <Button size="small" onClick={() => setViewMode('kanban')}
            startIcon={<ViewKanbanIcon sx={{ fontSize: 15 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2, py: 0.6, minWidth: 0,
              bgcolor: viewMode === 'kanban' ? '#1976d2' : 'transparent',
              color: viewMode === 'kanban' ? 'white' : '#666', borderRadius: 1,
              boxShadow: viewMode === 'kanban' ? '0 1px 4px rgba(25,118,210,0.3)' : 'none',
              '&:hover': { bgcolor: viewMode === 'kanban' ? '#1565c0' : '#e4e4e4' },
            }}>
            Сітка
          </Button>
          <Button size="small" onClick={() => setViewMode('list')}
            startIcon={<ListIcon sx={{ fontSize: 15 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2, py: 0.6, minWidth: 0,
              bgcolor: viewMode === 'list' ? '#1976d2' : 'transparent',
              color: viewMode === 'list' ? 'white' : '#666', borderRadius: 1,
              boxShadow: viewMode === 'list' ? '0 1px 4px rgba(25,118,210,0.3)' : 'none',
              '&:hover': { bgcolor: viewMode === 'list' ? '#1565c0' : '#e4e4e4' },
            }}>
            Список
          </Button>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="contained" startIcon={<VerifiedIcon sx={{ fontSize: 15 }} />}
            onClick={openQc}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
              bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, borderRadius: 1.5 }}>
            Контроль якості
          </Button>
          <Button size="small" variant="contained" startIcon={<PauseCircleIcon sx={{ fontSize: 15 }} />}
            onClick={openBreak}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
              bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, borderRadius: 1.5 }}>
            Перерва
          </Button>

          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button size="small" variant="contained" startIcon={<GroupsIcon sx={{ fontSize: 15 }} />}
              onClick={openShift}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
                bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, borderRadius: 1.5 }}>
              Зміна
            </Button>
            {unassignedCount > 0 && (
              <Box sx={{
                position: 'absolute', top: -7, right: -7,
                bgcolor: '#f44336', color: '#fff',
                borderRadius: '50%', minWidth: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, lineHeight: 1,
                border: '2px solid white', pointerEvents: 'none',
              }}>
                {unassignedCount > 99 ? '99+' : unassignedCount}
              </Box>
            )}
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button size="small" variant="contained" startIcon={<BuildIcon sx={{ fontSize: 15 }} />}
              onClick={() => setMaintDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
                bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' }, borderRadius: 1.5 }}>
              Обслуговування
            </Button>
            {maintTickets.length > 0 && (
              <Box sx={{
                position: 'absolute', top: -7, right: -7,
                bgcolor: '#f44336', color: '#fff',
                borderRadius: '50%', minWidth: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, lineHeight: 1,
                border: '2px solid white', pointerEvents: 'none',
              }}>
                {maintTickets.length > 99 ? '99+' : maintTickets.length}
              </Box>
            )}
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button size="small" variant="contained" startIcon={<LocalShippingIcon sx={{ fontSize: 15 }} />}
              onClick={() => setMatNeedOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
                bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' }, borderRadius: 1.5 }}>
              Матеріали
            </Button>
            {noMaterialCount > 0 && (
              <Box sx={{
                position: 'absolute', top: -7, right: -7,
                bgcolor: '#f44336', color: '#fff',
                borderRadius: '50%', minWidth: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, lineHeight: 1,
                border: '2px solid white', pointerEvents: 'none',
              }}>
                {noMaterialCount > 99 ? '99+' : noMaterialCount}
              </Box>
            )}
          </Box>
          <Button size="small" variant="outlined" startIcon={<BarChartIcon sx={{ fontSize: 15 }} />}
            onClick={() => setDowntimeDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 1.5, py: 0.6,
              borderColor: '#1565c0', color: '#1565c0', '&:hover': { bgcolor: '#e3f2fd' }, borderRadius: 1.5 }}>
            Простой ТПА
          </Button>
          <Tooltip title="Оновити">
            <IconButton onClick={refresh} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'kanban' ? (
        <GridView
          machines={machines}
          orders={activeOrders}
          allOrders={orders}
          products={products}
          customerOrders={customerOrders}
          onMachineClick={(machine, ords) => setMachineDrawer({ machine, orders: ords })}
          onQueueClick={(order) => setQueueDialog(order)}
          onUnscheduledClick={(line) => setPlanDialog({ coLine: line })} />
      ) : (
        <ListView orders={listOrders} machines={machines} onOrderClick={(o) => setListOrderDialog(o)} />
      )}

      {/* Drawers / Dialogs */}
      <MachineDrawer machine={machineDrawer?.machine} orders={machineDrawer?.orders || []}
        onClose={() => setMachineDrawer(null)} />
      <OrderDialog order={queueDialog} onClose={() => setQueueDialog(null)}
        onEdit={(o) => { setQueueDialog(null); setPlanDialog({ editingOrder: o }); }} />
      <OrderDialog order={listOrderDialog} onClose={() => setListOrderDialog(null)}
        onEdit={(o) => { setListOrderDialog(null); setPlanDialog({ editingOrder: o }); }} />
      <PlanOrderDialog
        open={Boolean(planDialog)}
        coLine={planDialog?.coLine}
        editingOrder={planDialog?.editingOrder}
        products={products}
        onClose={() => setPlanDialog(null)}
        onSaved={() => { setPlanDialog(null); refresh(); }}
      />

      {/* ── QC trigger dialog ─────────────────────────────────────────────── */}
      <Dialog open={qcOpen} onClose={() => setQcOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: 3, py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <VerifiedIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Контроль якості</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Ініціювати перевірку на станках</Typography>
            </Box>
          </Stack>
        </Box>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {qcSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
              <Typography variant="h6" fontWeight={700} color="#2e7d32">Перевірки створено!</Typography>
              <Typography variant="body2" color="text.secondary">
                ВКЯ побачить їх у канбані «Контроль якості».
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                Оберіть станки з запущеним замовленням — перевірка зʼявиться в канбані ВКЯ, пуш відкриє цей канбан.
                Зелена обводка = зараз є ЗВ «в роботі».
              </Typography>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                  СТАНКИ ({qcMachines.length} обрано)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {machines.map((m) => {
                    const sel = qcMachines.includes(m.id);
                    const hasActive = orders.some((o) => (
                      Number(o.machine) === Number(m.id)
                      && ['in_progress', 'paused'].includes(o.status)
                    ));
                    return (
                      <Chip key={m.id} label={m.identifier} clickable
                        onClick={() => setQcMachines((prev) => sel ? prev.filter((x) => x !== m.id) : [...prev, m.id])}
                        variant={sel ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 700, fontSize: 12,
                          bgcolor: sel ? '#1565c0' : 'transparent',
                          color: sel ? '#fff' : '#333',
                          borderColor: sel ? '#1565c0' : (hasActive ? '#2e7d32' : '#ccc'),
                          borderWidth: hasActive && !sel ? 2 : 1,
                          opacity: hasActive ? 1 : 0.55,
                          '&:hover': { bgcolor: sel ? '#0d47a1' : '#f0f4ff' } }} />
                    );
                  })}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                  ОПЕРАТОР (необовʼязково)
                </Typography>
                <TextField select size="small" fullWidth value={qcOperator}
                  onChange={(e) => setQcOperator(e.target.value)}>
                  <MenuItem value="">— будь-хто —</MenuItem>
                  {qcStaff.map((s) => <MenuItem key={s.id} value={s.id}>{s.full_name}</MenuItem>)}
                </TextField>
              </Box>
            </Box>
          )}
        </DialogContent>
        {!qcSuccess && (
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setQcOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
            <Button variant="contained" onClick={submitQc}
              disabled={qcSubmitting || qcMachines.length === 0}
              startIcon={qcSubmitting ? <CircularProgress size={14} color="inherit" /> : <VerifiedIcon />}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', borderRadius: 2 }}>
              {qcSubmitting ? 'Створення…' : `Створити перевірку (${qcMachines.length})`}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ── Add break dialog ──────────────────────────────────────────────── */}
      <Dialog open={breakOpen} onClose={() => setBreakOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#e65100', color: '#fff', px: 3, py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PauseCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Додати перерву</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Зупинка ТПА на плановий час</Typography>
            </Box>
          </Stack>
        </Box>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField select label="Станок" value={breakForm.machine} fullWidth required
              onChange={(e) => setBreakForm((f) => ({ ...f, machine: e.target.value }))}>
              <MenuItem value="">— обрати станок —</MenuItem>
              {machines.map((m) => <MenuItem key={m.id} value={m.id}>{m.identifier} — {m.name}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="Початок" type="datetime-local" fullWidth required
                value={breakForm.start_at} InputLabelProps={{ shrink: true }}
                onChange={(e) => setBreakForm((f) => ({ ...f, start_at: e.target.value }))} />
              <TextField label="Кінець" type="datetime-local" fullWidth required
                value={breakForm.end_at} InputLabelProps={{ shrink: true }}
                onChange={(e) => setBreakForm((f) => ({ ...f, end_at: e.target.value }))} />
            </Stack>
            {breakForm.start_at && breakForm.end_at && new Date(breakForm.end_at) > new Date(breakForm.start_at) && (
              <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1.5, px: 2, py: 1 }}>
                <Typography variant="body2" color="#e65100" fontWeight={600}>
                  Тривалість: {Math.round((new Date(breakForm.end_at) - new Date(breakForm.start_at)) / 60000)} хв
                </Typography>
              </Box>
            )}
            <TextField label="Причина перерви" multiline rows={2} fullWidth required
              value={breakForm.reason} placeholder="Напр.: технічне обслуговування, заміна прес-форми…"
              onChange={(e) => setBreakForm((f) => ({ ...f, reason: e.target.value }))} />
            {breakErr && <Typography color="error" variant="body2">{breakErr}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setBreakOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" onClick={submitBreak} disabled={breakSaving}
            startIcon={breakSaving ? <CircularProgress size={14} color="inherit" /> : <PauseCircleIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, borderRadius: 2 }}>
            {breakSaving ? 'Збереження…' : 'Зберегти перерву'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Machine operators dialog ──────────────────────────────────────── */}
      {/* ── Shift assignment dialog ─────────────────────────────── */}
      <Dialog open={shiftOpen} onClose={() => !shiftSaving && setShiftOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#2e7d32', color: '#fff', px: 3, py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <GroupsIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Призначення зміни</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Для кожного ТПА — оператор на сьогодні</Typography>
            </Box>
          </Stack>
        </Box>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {shiftLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Shift selector */}
              <FormControl fullWidth size="small">
                <InputLabel>Зміна</InputLabel>
                <Select value={selectedShiftId} label="Зміна"
                  onChange={(e) => setSelectedShiftId(e.target.value)}>
                  {shiftList.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              {shiftList.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Спочатку створіть зміни в Налаштуваннях → Персонал → Зміни.
                </Typography>
              )}

              {/* TPA → operator assignments */}
              {selectedShiftId && (() => {
                const onShift = shiftStaff.filter(
                  (s) => (s.shifts || []).map(String).includes(selectedShiftId)
                );
                // Поки в персоналі не привʼязано зміни — показуємо операторів за роллю
                const filteredOps = onShift.length > 0
                  ? onShift
                  : shiftStaff.filter((s) => /оператор/i.test(s.role_name || ''));
                return (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>
                    ТПА — ОПЕРАТОР ({machines.length} станків)
                  </Typography>
                  {onShift.length === 0 && (
                    <Typography variant="caption" color="warning.main">
                      У персоналі нікого не привʼязано до цієї зміни — показано всіх з роллю «Оператор».
                      Привʼязку можна задати в Налаштування → Персонал.
                    </Typography>
                  )}
                  {filteredOps.length === 0 && (
                    <Typography variant="caption" color="error.main">
                      Немає доступних операторів для вибору.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {machines.map((m) => {
                      const assigned = shiftAssign[m.id];
                      return (
                        <Paper key={m.id} variant="outlined" sx={{
                          p: 1.5, borderRadius: 2,
                          borderColor: assigned ? '#2e7d3244' : '#e0e0e0',
                          bgcolor: assigned ? '#f1f8e9' : '#fafafa',
                        }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box sx={{ flex: '0 0 90px' }}>
                              <Typography variant="body2" fontWeight={700}>{m.identifier}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>{m.name}</Typography>
                            </Box>
                            <FormControl size="small" sx={{ flex: 1 }}>
                              <Select value={shiftAssign[m.id] || ''}
                                onChange={(e) => setShiftAssign((prev) => ({ ...prev, [m.id]: e.target.value }))}
                                displayEmpty>
                                <MenuItem value=""><em>— не призначено —</em></MenuItem>
                                {filteredOps.map((op) => (
                                  <MenuItem key={op.id} value={op.id}>{op.full_name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShiftOpen(false)} disabled={shiftSaving} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" onClick={saveShift} disabled={shiftSaving || !selectedShiftId}
            startIcon={shiftSaving ? <CircularProgress size={14} color="inherit" /> : <GroupsIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, borderRadius: 2 }}>
            {shiftSaving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      </Box>

      {/* ── Maintenance kanban dialog (manager view) ── */}
      <Dialog open={maintDialogOpen} onClose={() => setMaintDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon color="secondary" />
          Обслуговування — активні заявки
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {maintTickets.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              Активних заявок на обслуговування немає
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, alignItems: 'start' }}>
              {MT_STATUSES.filter((s) => s.key !== 'done').map((col) => {
                const colTickets = maintTickets.filter((t) => t.status === col.key);
                if (colTickets.length === 0) return null;
                return (
                  <Box key={col.key}>
                    <Typography fontWeight={700} fontSize={13} mb={1} color="text.secondary">
                      {col.label} ({colTickets.length})
                    </Typography>
                    {colTickets.map((ticket) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        viewMode="manager"
                        onAction={handleMaintAction}
                      />
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintDialogOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* ── Maintenance comment dialog ── */}
      <Dialog open={Boolean(maintCommentDialog)} onClose={() => setMaintCommentDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{maintCommentDialog?.act?.label}</DialogTitle>
        <DialogContent>
          {maintCommentDialog?.act?.withSchedule && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Вкажіть час проведення обслуговування — оператор побачить його на плитці та отримає сповіщення за 5 хв.
              </Alert>
              <TextField
                label="Час обслуговування (обов'язково)"
                type="datetime-local"
                fullWidth
                sx={{ mb: 2 }}
                value={maintCommentScheduledAt}
                onChange={(e) => setMaintCommentScheduledAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
          {maintCommentDialog?.act?.needsComment && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Необхідно залишити коментар для відділу обслуговування.
              </Alert>
              <TextField
                label="Коментар (обов'язково)"
                multiline
                rows={4}
                fullWidth
                autoFocus={!maintCommentDialog?.act?.withSchedule}
                value={maintCommentText}
                onChange={(e) => setMaintCommentText(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintCommentDialog(null)} disabled={maintCommentSaving}>Скасувати</Button>
          <Button
            variant="contained"
            color={maintCommentDialog?.act?.color || 'primary'}
            onClick={handleMaintCommentSubmit}
            disabled={
              maintCommentSaving ||
              (maintCommentDialog?.act?.needsComment && !maintCommentText.trim()) ||
              (maintCommentDialog?.act?.withSchedule && !maintCommentScheduledAt)
            }
          >
            {maintCommentSaving ? 'Збереження…' : 'Підтвердити'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Downtime report dialog ── */}
      <Dialog open={downtimeDialogOpen} onClose={() => setDowntimeDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <BarChartIcon sx={{ color: '#1565c0' }} />
              <Typography fontWeight={700} fontSize={18}>Звіт по простоях ТПА</Typography>
            </Stack>
            <IconButton size="small" onClick={() => setDowntimeDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
              <TextField
                label="Від"
                type="datetime-local"
                size="small"
                value={downtimeDateFrom}
                onChange={(e) => setDowntimeDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 195 }}
              />
              <TextField
                label="До"
                type="datetime-local"
                size="small"
                value={downtimeDateTo}
                onChange={(e) => setDowntimeDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 195 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>ТПА</InputLabel>
                <Select
                  multiple
                  value={downtimeMachines}
                  onChange={(e) => setDowntimeMachines(e.target.value.filter(v => v !== '__all__'))}
                  label="ТПА"
                  renderValue={(sel) => sel.length === 0 ? 'Всі' : `Обрано: ${sel.length}`}
                >
                  <MenuItem value="__all__" dense onClick={(e) => { e.stopPropagation(); setDowntimeMachines(downtimeMachines.length === machines.length ? [] : machines.map(m => m.id)); }}>
                    <Checkbox checked={downtimeMachines.length === machines.length && machines.length > 0} indeterminate={downtimeMachines.length > 0 && downtimeMachines.length < machines.length} size="small" sx={{ py: 0 }} />
                    <ListItemText primary={<em>Обрати всі</em>} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                  {machines.map((m) => (
                    <MenuItem key={m.id} value={m.id} dense>
                      <Checkbox checked={downtimeMachines.includes(m.id)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={`${m.identifier} — ${m.name}`} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={loadDowntimeReport} disabled={downtimeLoading}
                size="small" sx={{ px: 2.5, fontWeight: 700, textTransform: 'none' }}>
                {downtimeLoading ? <CircularProgress size={15} sx={{ mr: 1 }} /> : null}
                Застосувати
              </Button>
            </Stack>
          </Paper>

          {downtimeLoading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}

          {!downtimeLoading && downtimeData && downtimeData.rows.length === 0 && (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary" fontSize={15}>Простоїв не знайдено за вказаний період</Typography>
            </Box>
          )}

          {!downtimeLoading && downtimeData && downtimeData.rows.length > 0 && (() => {
            // Validated categorical palette from dataviz spec (adjacent-pair safe)
            const SERIES_COLORS = ['#2a78d6','#eb6834','#1baf7a','#eda100','#e87ba4','#4a3aa7'];
            const reasons = downtimeData.reasons;
            const rows = downtimeData.rows;
            const totalAll = rows.reduce((s, r) => s + r.total_minutes, 0);

            // Horizontal stacked bar chart data
            const chartData = rows.map((row) => ({
              name: row.machine_identifier,
              fullName: row.machine_name,
              total: row.total_minutes,
              ...Object.fromEntries(reasons.map((r) => [r.label, row.by_reason[r.key] || 0])),
            }));

            const CustomTooltip = ({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const row = rows.find((r) => r.machine_identifier === label);
              return (
                <Paper elevation={4} sx={{ p: 1.5, minWidth: 200, borderRadius: 2 }}>
                  <Typography fontWeight={700} fontSize={13} mb={0.5}>{row?.machine_name} ({label})</Typography>
                  {payload.filter(p => p.value > 0).map((p) => (
                    <Stack key={p.name} direction="row" justifyContent="space-between" spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.fill, flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">{p.name}</Typography>
                      </Stack>
                      <Typography variant="caption" fontWeight={700}>{p.value} хв</Typography>
                    </Stack>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="caption" fontWeight={700}>Всього</Typography>
                    <Typography variant="caption" fontWeight={700}>{row?.total_minutes} хв</Typography>
                  </Stack>
                </Paper>
              );
            };

            return (
              <Box>
                {/* Summary stat tiles */}
                <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
                  <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Загальний простой</Typography>
                    <Typography fontWeight={800} fontSize={22} color="#d32f2f">{totalAll} хв</Typography>
                    <Typography variant="caption" color="text.secondary">{(totalAll / 60).toFixed(1)} год</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">ТПА з простоями</Typography>
                    <Typography fontWeight={800} fontSize={22}>{rows.length}</Typography>
                    <Typography variant="caption" color="text.secondary">з {machines.length} всього</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Найбільший простой</Typography>
                    <Typography fontWeight={800} fontSize={22}>{rows[0]?.machine_identifier}</Typography>
                    <Typography variant="caption" color="text.secondary">{rows[0]?.total_minutes} хв</Typography>
                  </Paper>
                  {reasons.length > 0 && (() => {
                    const topReason = reasons.reduce((best, r) => {
                      const sum = rows.reduce((s, row) => s + (row.by_reason[r.key] || 0), 0);
                      return sum > (best.sum || 0) ? { ...r, sum } : best;
                    }, {});
                    return (
                      <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Головна причина</Typography>
                        <Typography fontWeight={800} fontSize={14} mt={0.5}>{topReason.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{topReason.sum} хв</Typography>
                      </Paper>
                    );
                  })()}
                </Stack>

                {/* Horizontal stacked bar chart */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography fontWeight={600} fontSize={13} color="text.secondary" mb={1.5}>
                    Тривалість простою по ТПА (хв)
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 44)}>
                    <BarChart
                      data={chartData}
                      layout="vertical"
                      margin={{ top: 0, right: 60, left: 20, bottom: 0 }}
                      barCategoryGap="28%"
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e1e0d9" />
                      <XAxis type="number" unit=" хв" tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#52514e', fontWeight: 600 }} axisLine={false} tickLine={false} width={55} />
                      <RechartTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                      {reasons.map((r, i) => (
                        <Bar key={r.key} dataKey={r.label} stackId="a"
                          fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                          radius={i === reasons.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Detail table */}
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700, borderBottom: '2px solid #e0e0e0' }}>ТПА</TableCell>
                          {reasons.map((r, i) => (
                            <TableCell key={r.key} align="right" sx={{ fontWeight: 700, borderBottom: '2px solid #e0e0e0' }}>
                              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SERIES_COLORS[i % SERIES_COLORS.length] }} />
                                <span>{r.label}</span>
                              </Stack>
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700, borderBottom: '2px solid #e0e0e0' }}>Всього, хв</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.machine_id} hover>
                            <TableCell>
                              <Typography fontWeight={600} fontSize={13}>{row.machine_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.machine_identifier}</Typography>
                            </TableCell>
                            {reasons.map((r) => (
                              <TableCell key={r.key} align="right" sx={{ color: row.by_reason[r.key] > 0 ? '#0b0b0b' : '#ccc' }}>
                                {row.by_reason[r.key] > 0 ? row.by_reason[r.key] : '—'}
                              </TableCell>
                            ))}
                            <TableCell align="right">
                              <Typography fontWeight={700} color="#d32f2f">{row.total_minutes}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Total row */}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Разом</TableCell>
                          {reasons.map((r) => (
                            <TableCell key={r.key} align="right" sx={{ fontWeight: 700 }}>
                              {rows.reduce((s, row) => s + (row.by_reason[r.key] || 0), 0)}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#d32f2f' }}>{totalAll}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Materials Needed Dialog ─────────────────────────────────────────── */}
      <Dialog open={matNeedOpen} onClose={() => setMatNeedOpen(false)} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <LocalShippingIcon sx={{ color: '#00695c' }} />
            <Typography variant="h6" fontWeight={700}>Потреба в матеріалах</Typography>
          </Stack>
          <IconButton onClick={() => setMatNeedOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <TextField label="Від (дата)" type="date" size="small" value={matNeedDateFrom}
              onChange={(e) => setMatNeedDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 180 }} />
            <TextField label="До (дата)" type="date" size="small" value={matNeedDateTo}
              onChange={(e) => setMatNeedDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 180 }} />
            <Button variant="outlined" size="small" onClick={loadMatNeed} disabled={matNeedLoading}>
              {matNeedLoading ? <CircularProgress size={16} /> : 'Оновити'}
            </Button>
            <ToggleButtonGroup value={matNeedViewMode} exclusive size="small"
              onChange={(_, v) => v && setMatNeedViewMode(v)}>
              <ToggleButton value="aggregated" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Зведено</ToggleButton>
              <ToggleButton value="by_order" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>По замовленнях</ToggleButton>
            </ToggleButtonGroup>
            {pendingRequestsCount > 0 && (
              <Chip label={`Активних заявок: ${pendingRequestsCount}`} color="error" size="small" />
            )}
          </Stack>

          {matNeedLoading ? (
            <Box display="flex" justifyContent="center" pt={4}><CircularProgress /></Box>
          ) : matNeedData.length === 0 ? (
            <Typography color="text.secondary" align="center" pt={4}>Немає активних замовлень із матеріалами</Typography>
          ) : matNeedViewMode === 'aggregated' ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Матеріал</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Тип</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Потрібно</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Склад виробництва</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Основний склад</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>Нестача</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00695c' }}>Заявка на складі</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Замовлення</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matNeedData.map((row, idx) => {
                    const critical = row.shortage > 0;
                    const pct = row.quantity_needed > 0
                      ? Math.min(100, Math.round((row.prod_warehouse_qty / row.quantity_needed) * 100)) : 100;
                    const typeLabel = row.type === 'raw_material' ? 'Сировина' : row.type === 'dye' ? 'Барвник' : 'Пакування';
                    const typeColor = row.type === 'raw_material' ? '#1565c0' : row.type === 'dye' ? '#6a1b9a' : '#e65100';
                    const reqKey = `${row.type}:${row.material_id}`;
                    const existingReq = pendingReqByKey[reqKey];
                    return (
                      <TableRow key={idx} sx={{ bgcolor: critical ? '#fff8f8' : 'inherit', '&:hover': { bgcolor: critical ? '#ffecec' : '#f5f5f5' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell><Chip label={typeLabel} size="small" sx={{ bgcolor: typeColor, color: '#fff', fontSize: '0.7rem' }} /></TableCell>
                        <TableCell align="right">{row.quantity_needed.toFixed(2)} {row.unit}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                            <span>{row.prod_warehouse_qty.toFixed(2)}</span>
                            <Box sx={{ width: 50, height: 6, bgcolor: '#e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
                              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: pct < 30 ? '#d32f2f' : pct < 70 ? '#f57c00' : '#2e7d32', borderRadius: 3 }} />
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{row.main_stock_qty.toFixed(2)} {row.unit}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: critical ? '#d32f2f' : '#2e7d32' }}>
                          {critical ? `${row.shortage.toFixed(2)} ${row.unit}` : '✓'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          {existingReq ? (
                            <Stack spacing={0.2}>
                              <Typography variant="caption" sx={{ color: '#00695c', fontWeight: 700 }}>
                                {existingReq.quantity.toFixed(2)} {row.unit}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {new Date(existingReq.created_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="caption" color="text.secondary">
                            {row.orders.slice(0, 3).map((o) => `${orderRefFromRow(o)} ${o.product_name}`).join(', ')}
                            {row.orders.length > 3 && ` +${row.orders.length - 3}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant={critical ? 'contained' : 'outlined'}
                            color={critical ? 'error' : 'primary'}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            onClick={() => { setMatReqDialog(row); setMatReqQty(critical ? String(Math.ceil(row.shortage)) : ''); setMatReqNote(''); }}>
                            Заявка
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Замовлення</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Матеріал</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Тип</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Потрібно</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Склад виробництва</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>Нестача</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matNeedData.flatMap((row) =>
                    row.orders.map((o, oi) => {
                      const shortage = Math.max(0, o.quantity_needed - row.prod_warehouse_qty);
                      const critical = shortage > 0;
                      const typeLabel = row.type === 'raw_material' ? 'Сировина' : row.type === 'dye' ? 'Барвник' : 'Пакування';
                      const typeColor = row.type === 'raw_material' ? '#1565c0' : row.type === 'dye' ? '#6a1b9a' : '#e65100';
                      return (
                        <TableRow key={`${row.type}-${row.material_id}-${oi}`}
                          sx={{ bgcolor: critical ? '#fff8f8' : 'inherit', '&:hover': { bgcolor: '#f5f5f5' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{orderRefFromRow(o)} {o.product_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{o.machine_name} · {o.planned_end ? new Date(o.planned_end).toLocaleDateString('uk-UA') : '—'}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                          <TableCell><Chip label={typeLabel} size="small" sx={{ bgcolor: typeColor, color: '#fff', fontSize: '0.7rem' }} /></TableCell>
                          <TableCell align="right">{o.quantity_needed.toFixed(2)} {row.unit}</TableCell>
                          <TableCell align="right">{row.prod_warehouse_qty.toFixed(2)} {row.unit}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: critical ? '#d32f2f' : '#2e7d32' }}>
                            {critical ? `${shortage.toFixed(2)} ${row.unit}` : '✓'}
                          </TableCell>
                          <TableCell>
                            <Button size="small" variant={critical ? 'contained' : 'outlined'}
                              color={critical ? 'error' : 'primary'}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                              onClick={() => { setMatReqDialog(row); setMatReqQty(critical ? String(Math.ceil(shortage)) : ''); setMatReqNote(''); }}>
                              Заявка
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Звіти за зміну (з пуш-сповіщення) ─────────────────────────────── */}
      <Dialog open={Boolean(shiftReportsDialog)} onClose={closeShiftReportsDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Звіт(и) оператора за зміну
          <IconButton size="small" onClick={closeShiftReportsDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {shiftReportsDialog?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : shiftReportsDialog?.error ? (
            <Alert severity="warning">{shiftReportsDialog.error}</Alert>
          ) : (
            <Stack spacing={1.5}>
              {(shiftReportsDialog?.rows || []).map((r) => (
                <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {r.order_title_display || orderRefFromRow(r) || 'Замовлення'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.operator_name || 'Оператор'} · {fmtDt(r.created_at)}
                    {r.machine_identifier && !(r.order_title_display || '').includes(r.machine_identifier)
                      ? ` · ${r.machine_identifier}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    Виготовлено: <b>{r.produced_qty ?? '—'} шт</b>
                    {r.waste_kg != null && Number(r.waste_kg) > 0 ? <> · Відходи: <b>{r.waste_kg} кг</b></> : null}
                  </Typography>
                  {r.comment ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {r.comment}
                    </Typography>
                  ) : null}
                  {(r.order || r.production_order) ? (
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const oid = r.order || r.production_order;
                        closeShiftReportsDialog();
                        setSearchParams({ order: String(oid) }, { replace: true });
                        const found = orders.find((o) => Number(o.id) === Number(oid));
                        if (found) setListOrderDialog(found);
                      }}
                    >
                      Відкрити замовлення
                    </Button>
                  ) : null}
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShiftReportsDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Material Request sub-dialog ─────────────────────────────── */}
      <Dialog open={Boolean(matReqDialog)} onClose={() => setMatReqDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Заявка на переміщення</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{matReqDialog?.name}</strong> ({matReqDialog?.type === 'raw_material' ? 'Сировина' : matReqDialog?.type === 'dye' ? 'Барвник' : 'Пакування'})
          </Typography>
          <TextField label={`Кількість (${matReqDialog?.unit || 'кг'})`} type="number" size="small" fullWidth
            value={matReqQty} onChange={(e) => setMatReqQty(e.target.value)}
            inputProps={{ min: 0.001, step: 0.1 }} sx={{ mb: 2 }} />
          <TextField label="Примітка (необов'язково)" size="small" fullWidth multiline rows={2}
            value={matReqNote} onChange={(e) => setMatReqNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatReqDialog(null)}>Скасувати</Button>
          <Button variant="contained" color="success" onClick={handleCreateMatRequest}
            disabled={matReqSaving || !matReqQty || parseFloat(matReqQty) <= 0}>
            {matReqSaving ? <CircularProgress size={16} /> : 'Відправити заявку'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
